import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import styles from '@/modules/general-notes/styles.less';
import Notes from '@/modules/general-notes/components/Notes';
import NoteForm from '@/modules/general-notes/components/NoteForm';
import PrintNotes from '@/modules/general-notes/components/PrintNotes';
import {
  generalNotesQueryKeys,
  useCreateGeneralNoteMutation,
  useDeleteGeneralNoteMutation,
  useGeneralNotesQuery,
  useUpdateGeneralNoteItemStateMutation,
} from '@/entities/general-notes';
import {
  addDecedentNoteItem,
  deleteDecedentNoteItem,
  updateDecedentNote,
  updateDecedentNoteItemState,
} from '@/entities/general-notes/general-notes.api';
import { useCurrentDecedentId } from '@/app/providers/DecedentProvider';
import { Notification } from '@/common/utils';
import { useQueryClient } from '@tanstack/react-query';

const MAX_NOTES = 100;

const GeneralNotesModule = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);

  const decedentId = useCurrentDecedentId();

  const { isLoading, data } = useGeneralNotesQuery(decedentId, {
    queryKey: generalNotesQueryKeys.byDecedent(decedentId),
  });

  const createMutation = useCreateGeneralNoteMutation(decedentId, {
    onSuccess: () => {
      setIsCreating(false);
    },
    onError: (error) => {
      if (error?.message) {
        Notification.error(error.message);
      }
    },
  });

  const deleteMutation = useDeleteGeneralNoteMutation(decedentId);

  const updateItemStateMutation =
    useUpdateGeneralNoteItemStateMutation(decedentId);

  const notes = useMemo(
    () =>
      (data?.data ?? []).map((note) => ({
        id: note.id,
        title: note.title,
        expanded: true,
        items: (note.items ?? []).map((item) => ({
          id: item.id,
          label: item.text,
          done: item.isCompleted,
        })),
      })),
    [data]
  );

  const noteLimitReached = notes.length >= MAX_NOTES;

  const handleCreate = (payload) => {
    createMutation.mutate({
      ...payload,
      checklistItems:
        payload.checklistItems?.map((item) => item.label?.trim()).filter(Boolean) ?? [],
    });
  };

  const handleUpdate = async ({
    id,
    title,
    originalTitle,
    checklistItems = [],
    originalItems = [],
  }) => {
    const trimmedTitle = title?.trim();
    const items = checklistItems
      .map((item) => ({
        ...item,
        label: item.label?.trim() ?? '',
        done: !!item.done,
      }))
      .filter((item) => item.label);

    const originalById = new Map(originalItems.map((item) => [item.id, item]));
    const currentIds = new Set(items.filter((i) => i.id).map((i) => i.id));

    // API calls go straight to the API layer (not the mutation hooks) so the
    // list is refetched once at the end instead of after every request.
    const markDone = async (response, label) => {
      const createdItem = [...(response?.data?.items ?? [])]
        .reverse()
        .find((responseItem) => responseItem.text === label);
      if (createdItem?.id) {
        await updateDecedentNoteItemState(decedentId, id, createdItem.id, {
          isCompleted: true,
        });
      }
    };

    try {
      const independent = [];

      if (trimmedTitle && trimmedTitle !== originalTitle?.trim()) {
        independent.push(
          updateDecedentNote(decedentId, id, { title: trimmedTitle })
        );
      }

      // Removed items and items whose label changed (no edit endpoint: they
      // are deleted and re-created below).
      const toDelete = originalItems.filter(
        (item) =>
          !currentIds.has(item.id) ||
          items.some((i) => i.id === item.id && i.label !== item.label.trim())
      );
      toDelete.forEach((item) =>
        independent.push(deleteDecedentNoteItem(decedentId, id, item.id))
      );

      // Existing items with only the checkbox changed.
      items.forEach((item) => {
        const original = originalById.get(item.id);
        if (
          original &&
          original.label.trim() === item.label &&
          !!original.done !== item.done
        ) {
          independent.push(
            updateDecedentNoteItemState(decedentId, id, item.id, {
              isCompleted: item.done,
            })
          );
        }
      });

      await Promise.all(independent);

      // New / re-created items are added sequentially to keep their order.
      for (const item of items) {
        const original = originalById.get(item.id);
        const isNew = !item.id || !original;
        const labelChanged = original && original.label.trim() !== item.label;
        if (!isNew && !labelChanged) continue;

        const response = await addDecedentNoteItem(decedentId, id, {
          text: item.label,
        });
        if (item.done) await markDone(response, item.label);
      }
    } finally {
      await queryClient.invalidateQueries({
        queryKey: generalNotesQueryKeys.byDecedent(decedentId),
      });
    }
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  const handleToggleItem = (noteId, itemId, isCompleted) => {
    updateItemStateMutation.mutate({
      noteId,
      itemId,
      payload: { isCompleted },
    });
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h3 className={styles.pageHeaderTitle}>
          {t('General Notes')}{' '}
        </h3>
        <div className={styles.pageHeaderBtns}>
          <Tooltip
            classes={noteLimitReached ? { tooltip: styles.limitTooltip } : undefined}
            title={
              noteLimitReached
                ? t(
                    'Note limit reached (max 100). Please delete an existing note to add a new one.'
                  )
                : ''
            }
          >
            <span>
              <Button
                variant="contained"
                color="success"
                type="success"
                onClick={() => setIsCreating(true)}
                disabled={noteLimitReached}
                startIcon={<AddIcon />}
              >
                {t('New Note')}
              </Button>
            </span>
          </Tooltip>
          <PrintNotes notes={notes} />
        </div>
      </div>
      <div className={styles.pageBody}>
        {!!notes.length && <span className={styles.pageBodyLegend}>{notes.length} {' '} {notes.length > 1 ? t('notes') : t('note')}</span>}
        {isCreating && (
          <NoteForm
            onCancel={() => setIsCreating(false)}
            onSave={handleCreate}
            disableChecklistToggle
            loading={createMutation.isPending}
          />
        )}
        <Notes
          showEmptyState={!notes.length && !isCreating}
          loading={isLoading}
          list={notes}
          onSaveNote={handleUpdate}
          onDeleteNote={handleDelete}
          onToggleItem={handleToggleItem}
        />
      </div>
    </div>
  );
};

export default GeneralNotesModule;
